/**
 * The channel's rarity ladder.
 *
 * Theirs was a compile-time union declared four times across two languages,
 * with the colours re-hardcoded in about ten more tables, and no way for a
 * streamer to have their own tiers at all. Here it is a list somebody edits.
 *
 * Two things this screen has to keep straight:
 *
 *   - the KEY is identity and never changes. Renaming Epic to Mythic is a
 *     label edit, and every copy ever minted follows without a migration.
 *   - there is no delete. Retiring stops a tier being offered; deleting it
 *     would strand every copy carrying it, which is how their audio rarity
 *     ended up recovered by substring-matching a document id.
 */
import { useState, type ReactNode } from 'react';
import type { RarityTier } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Badge, Button, Failed, Spinner } from '../components/primitives.jsx';
import { Field, Num, Text } from '../components/form.jsx';
import { ApiError } from '../lib/api.js';
import { rarity } from '../lib/tomo.js';
import { useAsync } from '../useAsync.js';
import { useSession } from '../Session.jsx';

export function Rarities({ channelId }: { channelId: string }): ReactNode {
  const session = useSession();
  const canEdit = session.channels
    .find((c) => c.id === channelId)?.scopes.includes('rewards:write') ?? false;

  const [result, retry] = useAsync((signal) => rarity.list(channelId, signal), [channelId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = (run: () => Promise<unknown>): void => {
    setBusy(true);
    setError(null);
    run().then(
      () => { setBusy(false); retry(); },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof ApiError ? err.message : 'Something went wrong.');
      }
    );
  };

  return (
    <Shell channelId={channelId}>
      <h1 className="text-xl font-semibold tracking-tight">Rarities</h1>
      <p className="mt-1 text-sm text-ink-400">
        Your own tiers, in your own order. Name them whatever your community calls them.
      </p>

      {error ? <div className="mt-6"><Failed message={error} /></div> : null}

      {result.state === 'loading' ? <Spinner label="Loading your ladder…" /> : null}
      {result.state === 'failed' ? (
        <div className="mt-8">
          <Failed message="Could not load the ladder." detail={result.error.message} onRetry={retry} />
        </div>
      ) : null}

      {result.state === 'ready' ? (
        <>
          <ul className="mt-8 divide-y divide-ink-800 rounded-lg border border-ink-800">
            {result.value.map((tier) => (
              <Row
                key={tier.key}
                tier={tier}
                canEdit={canEdit}
                busy={busy}
                onSave={(next) => { act(() => rarity.put(channelId, next)); }}
                onRetire={(retired) => { act(() => rarity.retire(channelId, tier.key, retired)); }}
              />
            ))}
          </ul>

          {canEdit ? (
            <AddTier
              busy={busy}
              existing={result.value.map((t) => t.key)}
              onAdd={(tier) => { act(() => rarity.put(channelId, tier)); }}
            />
          ) : null}
        </>
      ) : null}
    </Shell>
  );
}

type Draft = Omit<RarityTier, 'channel_id' | 'retired'>;

function Row(
  { tier, canEdit, busy, onSave, onRetire }:
  {
    tier: RarityTier; canEdit: boolean; busy: boolean;
    onSave: (t: Draft) => void; onRetire: (retired: boolean) => void;
  }
): ReactNode {
  const [label, setLabel] = useState(tier.label);
  const [color, setColor] = useState(tier.color);
  const [order, setOrder] = useState(tier.order);
  const changed = label !== tier.label || color !== tier.color || order !== tier.order;

  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3">
      <Swatch color={color} />

      <span className="min-w-40 flex-1">
        {canEdit ? (
          <Text value={label} onChange={setLabel} maxLength={40} />
        ) : (
          <span className="text-ink-50">{tier.label}</span>
        )}
        {/* The key never changes. Shown so it is obvious that renaming is safe. */}
        <span className="mt-1 block font-mono text-xs text-ink-700">{tier.key}</span>
      </span>

      {canEdit ? (
        <>
          <span className="w-28">
            <Text value={color} onChange={setColor} placeholder="#a855f7" maxLength={7} />
          </span>
          <span className="w-20">
            <Num value={order} onChange={setOrder} min={0} max={999} />
          </span>
        </>
      ) : null}

      {tier.retired ? <Badge tone="warn">Retired</Badge> : null}

      {canEdit ? (
        <span className="flex items-center gap-2">
          <Button disabled={busy || !changed} onClick={() => { onSave({ key: tier.key, label, color, icon: tier.icon, order }); }}>
            Save
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => { onRetire(!tier.retired); }}>
            {tier.retired ? 'Bring back' : 'Retire'}
          </Button>
        </span>
      ) : null}
    </li>
  );
}

function AddTier(
  { busy, existing, onAdd }:
  { busy: boolean; existing: string[]; onAdd: (t: Draft) => void }
): ReactNode {
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('#a855f7');
  const [order, setOrder] = useState(60);

  // The key is derived from the label ONCE, at creation, and is immutable
  // afterwards. Deriving it keeps a streamer from having to understand the
  // distinction on the way in, while the row above makes it visible after.
  const key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 31);
  const valid = /^[a-z][a-z0-9_]*$/.test(key) && !existing.includes(key);

  return (
    <form
      className="mt-8 flex max-w-2xl flex-wrap items-end gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        onAdd({ key, label, color, icon: '', order });
        setLabel('');
      }}
    >
      <span className="min-w-48 flex-1">
        <Field label="New tier" hint={key ? `key: ${key}` : 'The key is set from the name and never changes.'}>
          <Text value={label} onChange={setLabel} placeholder="Secret Rare" maxLength={40} />
        </Field>
      </span>
      <span className="w-32">
        <Field label="Colour"><Text value={color} onChange={setColor} maxLength={7} /></Field>
      </span>
      <span className="w-24">
        <Field label="Order"><Num value={order} onChange={setOrder} min={0} max={999} /></Field>
      </span>
      <Button type="submit" disabled={busy || label.length === 0 || !valid}>Add</Button>
      {label.length > 0 && !valid ? (
        <p className="w-full text-xs text-warn-400">
          {existing.includes(key) ? `You already have a tier keyed ${key}.` : 'Give it a name with some letters in it.'}
        </p>
      ) : null}
    </form>
  );
}

function Swatch({ color }: { color: string }): ReactNode {
  const valid = /^#[0-9a-fA-F]{6}$/.test(color);
  return (
    <span
      aria-hidden
      className="size-6 shrink-0 rounded border border-ink-700"
      style={{ background: valid ? color : 'transparent' }}
    />
  );
}
